import { AutoStack, OrderIdPicker } from 'components'
import { Field, FieldArray, FieldProps, FormikConsumer } from 'formik'
import { FormikDropdown, FormikSpinButton } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { OrderIdDistribution, Proving } from 'model'
import { IconButton, IDropdownOption, Label } from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

export type ProvingDetailsOrderIdDistributionFormValues = Proving

export class ProvingDetailsOrderIdDistributionFormSchema {
  constructor() {
    return Yup.object().shape({
      orderIdDistribution: Yup.array().test(
        'sum',
        'Die Summe der Auftragsnummern muss 100% betragen',
        (value: OrderIdDistribution[]) => {
          if (
            value &&
            value.length === 1 &&
            value[0] &&
            value[0].percentage === null
          ) {
            return true
          } else {
            return (
              value.reduce((sum, x) => sum + (x.percentage || 0), 0) === 100
            )
          }
        }
      ),
    })
  }
}

export enum OrderIdDistributionFormType {
  SingleOrderId,
  MultipleOrderIdDistribution,
}

export const ProvingDetailsOrderIdDistributionFormWithContext: React.FunctionComponent<{
  orderIds: string[]
  formType: OrderIdDistributionFormType
  ctx: any
}> = ({ orderIds, formType, ctx }) => {
  const { t } = useTranslation()

  const orderIdOptions = [
    ...orderIds.map(
      (orderId) =>
        ({
          key: orderId,
          text: orderId,
        } as IDropdownOption)
    ),
  ]
  const orderIdOptionsNoEmpty = orderIds.map(
    (orderId) =>
      ({
        key: orderId,
        text: orderId,
      } as IDropdownOption)
  )

  return (
    <FormStack>
      {formType === OrderIdDistributionFormType.MultipleOrderIdDistribution && (
        <FieldStack>
          <Label>{'Auftragsnummern'}</Label>
          <FieldArray name="orderIdDistribution">
            {({ form, name, ...helpers }) => {
              const values: { orderId: string; percentage: number }[] =
                form.values[name] || []

              return (
                <AutoStack tokens={{ childrenGap: 4 }}>
                  {values.map((x: any, i: any) => (
                    <AutoStack
                      horizontal={true}
                      verticalAlign="center"
                      tokens={{ childrenGap: 8 }}
                      key={i}
                    >
                      <Field
                        name={`orderIdDistribution[${i}].orderId`}
                        render={(props: any) => (
                          <FormikDropdown
                            styles={{ dropdown: { width: 175 } }}
                            placeholders={{
                              default: t('section.orderIds.field.orderId'),
                            }}
                            options={orderIdOptions}
                            {...props}
                          />
                        )}
                      />
                      <Field
                        name={`orderIdDistribution[${i}].percentage`}
                        render={(props: FieldProps<any>) => (
                          <FormikSpinButton
                            min={0}
                            label="%"
                            labelPosition={Position.end}
                            max={100}
                            step={5}
                            {...props}
                          />
                        )}
                      />
                      {i !== 0 && (
                        <IconButton
                          styles={{ root: { color: 'red' } }}
                          iconProps={{ iconName: 'Delete' }}
                          onClick={() => helpers.remove(i)}
                        />
                      )}
                    </AutoStack>
                  ))}
                  <OrderIdPicker
                    blacklist={values
                      .filter((val) =>
                        orderIdOptions.find((tag) => tag.key === val.orderId)
                      )
                      .map((v) => v.orderId)}
                    styles={{ text: { width: 175 } }}
                    inputProps={{
                      placeholder: '+ ' + t('section.orderIds.field.orderId'),
                    }}
                    initialValues={orderIds}
                    itemLimit={1}
                    onItemSelected={(item) => {
                      if (item) {
                        helpers.push({
                          orderId: item.key,
                          percentage:
                            100 -
                            values.reduce(
                              (sum, x) => sum + Number(x.percentage || 0),
                              0
                            ),
                        })
                        return null
                      }
                      return null
                    }}
                  />

                  <AutoStack
                    horizontal={true}
                    verticalAlign="center"
                    tokens={{ childrenGap: 8 }}
                  >
                    <Box width="175px" style={{ textAlign: 'right' }} px={1}>
                      {t('section.orderIds.field.sum')}
                    </Box>
                    <Box px={1}>
                      {values.reduce((sum, x) => sum + x.percentage, 0)}%
                    </Box>
                  </AutoStack>
                  <div style={{ color: 'red' }}>
                    {form.errors.orderIdDistribution}
                  </div>
                </AutoStack>
              )
            }}
          </FieldArray>
        </FieldStack>
      )}
      {formType === OrderIdDistributionFormType.SingleOrderId && (
        <FieldStack horizontal={true}>
          <FieldStackItem
            name={'orderIdDistribution[0].orderId'}
            render={(fieldProps) => (
              <FormikDropdown
                label={t('field.orderId.label')}
                required={true}
                {...fieldProps}
                options={orderIdOptionsNoEmpty}
              />
            )}
          />
        </FieldStack>
      )}
    </FormStack>
  )
}
export const ProvingDetailsOrderIdDistributionForm: React.FunctionComponent<{
  orderIds: string[]
  formType: OrderIdDistributionFormType
}> = ({ orderIds, formType }) => (
  <FormikConsumer>
    {(ctx) => (
      <ProvingDetailsOrderIdDistributionFormWithContext
        orderIds={orderIds}
        formType={formType}
        ctx={ctx}
      />
    )}
  </FormikConsumer>
)
